import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {SearchDictsService} from "./search-dicts.service";

export type MapboxResult = any;
export type MapboxSearchResponse = {
  features: MapboxResult[]
  query: string[]
  type: "FeatureCollection"
}
export type SearchResultIcon = 'boundary' | 'area' | 'location';
export type SearchResult<T> = {
  icon: SearchResultIcon;
  title: string;
  description: string;
  data: T;
};

export type SearchPointItem = {
  hint: string;
  label: string;
  type: 'coors';
  x: string;
  y: string;
};
export const isSearchPointItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchPointItem => {
  return data.type === 'coors';
};

export type SearchPlotItem = {
  bbox: unknown[];
  coordinates: number[][][];
  hint: string;
  label: string;
  type: 'plot';
  uuid: string;
};
export const isSearchPlotItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchPlotItem => {
  return data.type === 'plot';
};

export type SearchOtherItem = {
  hint: string;
  label: string;
  type: 'desa' | 'kecamatan' | 'kabupaten' | 'province';
  zoneId: string;
  zoneType: string;
};
export const isSearchOtherItem = (
  data: SearchPointItem | SearchPlotItem | SearchOtherItem,
): data is SearchOtherItem => {
  const possibleTypes = ['desa', 'kecamatan', 'kabupaten', 'desa', 'province'];
  return possibleTypes.includes(data.type);
};

export type SearchApiItem = SearchPointItem | SearchPlotItem | SearchOtherItem;

type SearchApiResponse = {
  list: SearchApiItem[];
};

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private http = inject(HttpClient);
  private searchDicts = inject(SearchDictsService);

  public searchByString(searchStr: string): Observable<SearchResult<SearchApiItem>[]> {
    return this.http
      .post<SearchApiResponse>(`${environment.apiUrl}/api/search`, { search: searchStr })
      .pipe(
        map((data: SearchApiResponse) => {
          return data.list
            .map(
              (
                item: SearchPlotItem | SearchPointItem | SearchOtherItem,
              ): SearchResult<SearchApiItem> | null => {
                if (isSearchPointItem(item)) {
                  return {
                    icon: 'location',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchPlotItem(item)) {
                  return {
                    icon: 'area',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchOtherItem(item)) {
                  return {
                    icon: 'boundary',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                }

                return null;
              },
            )
            .filter((it: SearchResult<SearchApiItem> | null): it is SearchResult<SearchApiItem> => {
              return it !== null && it !== undefined;
            });
        }),
      );
  }

  public searchByNib(
    searchStr: string,
    searchNibData: { desa: string; kabupaten: string; ownershipType: string },
  ): Observable<SearchResult<SearchApiItem>[]> {
    return this.http
      .post<SearchApiResponse>(`${environment.apiUrl}/api/search`, {
        search: searchStr,
        ...searchNibData,
      })
      .pipe(
        map((data: SearchApiResponse) => {
          return data.list
            .map(
              (
                item: SearchPlotItem | SearchPointItem | SearchOtherItem,
              ): SearchResult<SearchApiItem> | null => {
                if (isSearchPointItem(item)) {
                  return {
                    icon: 'location',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchPlotItem(item)) {
                  return {
                    icon: 'area',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                } else if (isSearchOtherItem(item)) {
                  return {
                    icon: 'boundary',
                    title: item.label,
                    description: item.hint,
                    data: item,
                  };
                }

                return null;
              },
            )
            .filter((it: SearchResult<SearchApiItem> | null): it is SearchResult<SearchApiItem> => {
              return it !== null && it !== undefined;
            });
        }),
      );
  }

  searchByMapbox(term: string, lngLat: {lng: string, lat: string}): Observable<MapboxResult[]> {
    return this.http
      .post<MapboxSearchResponse>(`${environment.apiUrl}/api/search/mapbox`, {
        search: term,
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      }).pipe(map((it) => it.features))
  }
}
